import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Building2, TrendingUp, CheckCircle2, XCircle, Clock,
  Plus, Calendar, Users, Euro, Package, Bell, Settings, LogOut,
  Sparkles, Camera, BarChart3, MessageSquare, MapPin, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import CreateDealModal from '@/components/CreateDealModal';
import CreateOfferModal from '@/components/CreateOfferModal';
import PhotoUploader from '@/components/PhotoUploader';
import UnsoldRoomsManager from '@/components/UnsoldRoomsManager';
import MatchingSuggestions from '@/components/MatchingSuggestions';
import RequestCardModern from '@/components/RequestCardModern';
import AISearchChat from '@/components/AISearchChat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PropertyOwnerDashboardNew = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data states
  const [deals, setDeals] = useState([]);
  const [requests, setRequests] = useState([]);
  const [myOffers, setMyOffers] = useState([]);
  const [dealOffers, setDealOffers] = useState([]);
  const [property, setProperty] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [unsoldRooms, setUnsoldRooms] = useState([]);
  const [counterOffers, setCounterOffers] = useState([]);
  
  // Modal states
  const [showCreateDealModal, setShowCreateDealModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  useEffect(() => {
    // Wait for auth context to load before checking authentication
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      
      if (!token || !userData) {
        navigate('/');
        return false;
      }
      
      try {
        const parsedUser = JSON.parse(userData);
        if (parsedUser.role !== 'property_owner') {
          navigate('/');
          return false;
        }
        return true;
      } catch {
        navigate('/');
        return false;
      }
    };
    
    if (checkAuth()) {
      fetchAllData();
    }
  }, [navigate]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch property info
      const propertiesRes = await axios.get(`${BACKEND_URL}/api/properties`, { headers });
      let currentProperty = null;
      if (propertiesRes.data && propertiesRes.data.length > 0) {
        currentProperty = propertiesRes.data[0];
        setProperty(currentProperty);
      }

      // Fetch my deals
      try {
        const dealsRes = await axios.get(`${BACKEND_URL}/api/deals`, { headers });
        setDeals(dealsRes.data.deals || dealsRes.data || []);
      } catch (e) {
        console.log('No deals found');
      }

      // Fetch travel requests
      try {
        const requestsRes = await axios.get(`${BACKEND_URL}/api/requests`);
        setRequests(requestsRes.data.requests || []);
      } catch (e) {
        console.log('No requests found');
      }

      // Fetch my offers
      try {
        const offersRes = await axios.get(`${BACKEND_URL}/api/offers/by-owner`, { headers });
        setMyOffers(offersRes.data.offers || []);
      } catch (e) {
        console.log('No offers found');
      }

      // Fetch deal counter-offers received
      try {
        const dealOffersRes = await axios.get(`${BACKEND_URL}/api/deal-offers/by-owner`, { headers });
        setDealOffers(dealOffersRes.data.deal_offers || dealOffersRes.data.offers || []);
      } catch (e) {
        console.log('No deal offers found');
      }

      // Fetch AI suggestions for owner
      try {
        const suggestionsRes = await axios.get(
          `${BACKEND_URL}/api/matching/suggestions/for-owner${currentProperty ? `?property_id=${currentProperty.id}` : ''}`,
          { headers }
        );
        setAiSuggestions(suggestionsRes.data.suggestions || []);
      } catch (e) {
        console.log('No AI suggestions:', e.message);
      }

      // Fetch unsold rooms analysis
      try {
        const unsoldRes = await axios.get(`${BACKEND_URL}/api/matching/unsold-rooms`, { headers });
        setUnsoldRooms(unsoldRes.data.unsold_rooms || []);
      } catch (e) {
        console.log('No unsold rooms data:', e.message);
      }

      // Fetch counter-offers received from travelers
      try {
        const counterOffersRes = await axios.get(`${BACKEND_URL}/api/counter-offers/by-owner`, { headers });
        setCounterOffers(counterOffersRes.data.counter_offers || []);
      } catch (e) {
        console.log('No counter-offers:', e.message);
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptDealOffer = async (offerId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/deal-offers/${offerId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Counter-offerta accettata!');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleRejectDealOffer = async (offerId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/deal-offers/${offerId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Counter-offerta rifiutata');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleAcceptCounterOffer = async (counterOfferId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${BACKEND_URL}/api/counter-offers/${counterOfferId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Contro-offerta accettata! Il viaggiatore può ora procedere al pagamento.');
      fetchAllData();
    } catch (error) {
      console.error('Error accepting counter-offer:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'accettare la contro-offerta');
    }
  };

  const handleRejectCounterOffer = async (counterOfferId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${BACKEND_URL}/api/counter-offers/${counterOfferId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.info('Contro-offerta rifiutata');
      fetchAllData();
    } catch (error) {
      console.error('Error rejecting counter-offer:', error);
      toast.error(error.response?.data?.detail || 'Errore nel rifiutare la contro-offerta');
    }
  };

  const getStats = () => {
    return {
      totalDeals: deals.length,
      activeDeals: deals.filter(d => new Date(d.check_in) >= new Date()).length,
      totalRequests: requests.length,
      pendingOffers: myOffers.filter(o => o.status === 'pending').length,
      acceptedOffers: myOffers.filter(o => o.status === 'accepted').length,
      pendingDealOffers: dealOffers.filter(o => o.status === 'pending').length,
      pendingCounterOffers: counterOffers.filter(co => co.status === 'pending').length
    };
  };

  const stats = getStats();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50/30 to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-slate-200 border-t-orange-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 text-lg">Caricamento dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50/30 to-slate-50">
      {/* Header */}
      <nav className="backdrop-blur-xl bg-white/80 border-b border-slate-200/50 px-6 py-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Home
            </Button>
            <div className="h-6 w-px bg-slate-300" />
            <div>
              <h1 className="text-xl font-bold text-slate-900">Dashboard Proprietario</h1>
              <p className="text-xs text-slate-600">{property?.name || 'La tua struttura'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon">
              <Bell className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2 px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg">
              <Building2 className="w-4 h-4 text-orange-600" />
              <span className="text-sm font-medium text-orange-900">{user?.full_name}</span>
            </div>
            <Button variant="outline" onClick={() => { logout(); navigate('/'); }}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-7 mb-8 bg-white shadow-sm">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" /> Overview
            </TabsTrigger>
            <TabsTrigger value="ai-suggestions" className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> AI Match ({aiSuggestions.length})
            </TabsTrigger>
            <TabsTrigger value="deals" className="flex items-center gap-2">
              <Package className="w-4 h-4" /> Deals ({stats.totalDeals})
            </TabsTrigger>
            <TabsTrigger value="requests" className="flex items-center gap-2">
              <Users className="w-4 h-4" /> Richieste ({stats.totalRequests})
            </TabsTrigger>
            <TabsTrigger value="offers" className="flex items-center gap-2">
              <Euro className="w-4 h-4" /> Offerte ({myOffers.length})
            </TabsTrigger>
            <TabsTrigger value="counter-offers" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" /> Counter ({dealOffers.length + counterOffers.length})
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="w-4 h-4" /> Gestione
            </TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="p-6 bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
                <div className="flex items-center justify-between mb-3">
                  <Package className="w-10 h-10 text-orange-600" />
                  <Badge className="bg-orange-600 text-white">Attivi</Badge>
                </div>
                <p className="text-sm text-orange-700 font-medium">Deals Attivi</p>
                <p className="text-4xl font-black text-orange-900 mt-2">{stats.activeDeals}</p>
                <p className="text-xs text-orange-600 mt-1">su {stats.totalDeals} totali</p>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                <div className="flex items-center justify-between mb-3">
                  <TrendingUp className="w-10 h-10 text-blue-600" />
                  <Badge className="bg-blue-600 text-white">Nuove</Badge>
                </div>
                <p className="text-sm text-blue-700 font-medium">Richieste Disponibili</p>
                <p className="text-4xl font-black text-blue-900 mt-2">{stats.totalRequests}</p>
                <p className="text-xs text-blue-600 mt-1">da valutare</p>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
                <div className="flex items-center justify-between mb-3">
                  <CheckCircle2 className="w-10 h-10 text-emerald-600" />
                  <Badge className="bg-emerald-600 text-white">Confermate</Badge>
                </div>
                <p className="text-sm text-emerald-700 font-medium">Offerte Accettate</p>
                <p className="text-4xl font-black text-emerald-900 mt-2">{stats.acceptedOffers}</p>
                <p className="text-xs text-emerald-600 mt-1">{stats.pendingOffers} in attesa</p>
              </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-amber-600" />
                  Azioni Richieste
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <span className="text-sm text-slate-700">Counter-offerte da valutare</span>
                    <Badge className="bg-amber-600 text-white">{stats.pendingDealOffers}</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <span className="text-sm text-slate-700">Offerte in attesa</span>
                    <Badge className="bg-blue-600 text-white">{stats.pendingOffers}</Badge>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-orange-600" />
                  La Mia Struttura
                </h3>
                {property ? (
                  <div className="space-y-2">
                    <p className="text-2xl font-bold text-slate-900">{property.name}</p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      📍 {property.city}
                    </p>
                    <Button variant="outline" className="w-full mt-4" onClick={() => toast.info('Feature in arrivo')}>
                      <Settings className="w-4 h-4 mr-2" />
                      Gestisci Struttura
                    </Button>
                  </div>
                ) : (
                  <p className="text-slate-600">Nessuna struttura trovata</p>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* DEALS TAB */}
          <TabsContent value="deals" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">I Miei Deals Last-Minute</h2>
                <p className="text-slate-600 text-sm">Gestisci le tue offerte speciali</p>
              </div>
              <Button
                onClick={() => setShowCreateDealModal(true)}
                className="bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800"
              >
                <Plus className="w-4 h-4 mr-2" />
                Crea Nuovo Deal
              </Button>
            </div>

            {deals.length === 0 ? (
              <Card className="p-16 text-center">
                <Package className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600 text-lg mb-4">Non hai ancora creato deals</p>
                <Button onClick={() => setShowCreateDealModal(true)} className="bg-orange-600">
                  Crea il tuo primo deal
                </Button>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {deals.map((deal) => (
                  <Card key={deal.id} className="overflow-hidden hover:shadow-xl transition-all">
                    <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-4 text-white">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-bold text-lg">{deal.room_type_name}</h3>
                          <p className="text-orange-100 text-xs">{deal.property_details?.name}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-black">€{deal.discounted_price}</div>
                          <div className="text-xs line-through text-orange-200">€{deal.original_price}</div>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 space-y-3">
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-blue-600" />
                        <span className="text-slate-600">{deal.check_in} → {deal.check_out}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Users className="w-4 h-4 text-blue-600" />
                        <span className="text-slate-600">{deal.available_units} unità disponibili</span>
                      </div>
                      {deal.description && (
                        <p className="text-xs text-slate-600 bg-slate-50 p-2 rounded">{deal.description}</p>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* REQUESTS TAB */}
          <TabsContent value="requests" className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Richieste Viaggiatori</h2>
              <p className="text-slate-600 text-sm">Fai un'offerta alle richieste che ti interessano. I dati del viaggiatore sono oscurati per privacy.</p>
            </div>

            {requests.length === 0 ? (
              <Card className="p-16 text-center">
                <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 text-lg">Nessuna richiesta disponibile al momento</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {requests.map((request) => {
                  const myOfferExists = myOffers.some(o => o.request_id === request.id);
                  
                  return (
                    <RequestCardModern
                      key={request.id}
                      request={request}
                      isOwnerView={true}
                      hasMyOffer={myOfferExists}
                      interestCount={request.interest_count || 0}
                      onMakeOffer={(req) => setSelectedRequest(req)}
                    />
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* MY OFFERS TAB */}
          <TabsContent value="offers" className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Le Mie Offerte</h2>
              <p className="text-slate-600 text-sm">Offerte inviate ai viaggiatori</p>
            </div>

            {myOffers.length === 0 ? (
              <Card className="p-16 text-center">
                <p className="text-slate-600 text-lg">Non hai ancora inviato offerte</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {myOffers.map((offer) => (
                  <Card key={offer.id} className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-bold text-lg text-slate-900">{offer.property_name}</h3>
                        <p className="text-sm text-slate-600 mt-1">{offer.room_description}</p>
                        {offer.notes && (
                          <p className="text-xs text-slate-600 bg-slate-50 p-2 rounded mt-2">{offer.notes}</p>
                        )}
                      </div>
                      <div className="text-right ml-4">
                        <div className="text-3xl font-black text-orange-600">€{offer.offered_price}</div>
                        <Badge className={`mt-2 ${
                          offer.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                          offer.status === 'accepted' ? 'bg-emerald-100 text-emerald-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {offer.status === 'pending' && <Clock className="w-3 h-3 mr-1 inline" />}
                          {offer.status === 'accepted' && <CheckCircle2 className="w-3 h-3 mr-1 inline" />}
                          {offer.status === 'rejected' && <XCircle className="w-3 h-3 mr-1 inline" />}
                          {offer.status === 'pending' ? 'In Attesa' :
                           offer.status === 'accepted' ? 'Accettata' : 'Rifiutata'}
                        </Badge>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* COUNTER-OFFERS TAB */}
          <TabsContent value="counter-offers" className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Contro-Offerte Ricevute</h2>
              <p className="text-slate-600 text-sm">Viaggiatori che hanno fatto contro-offerte sulle tue offerte</p>
            </div>

            {/* COUNTER-OFFERS ON REGULAR OFFERS */}
            {counterOffers.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-purple-600" />
                  Contro-offerte sulle Offerte ({counterOffers.filter(co => co.status === 'pending').length} da valutare)
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  {counterOffers.map((co) => (
                    <Card key={co.id} className="p-6 border-l-4 border-l-purple-500 hover:shadow-lg transition-all">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className="bg-purple-100 text-purple-800">Contro-Offerta</Badge>
                            <span className="text-sm text-slate-600">da {co.traveler?.name || 'Viaggiatore'}</span>
                          </div>
                          <h3 className="font-bold text-lg text-slate-900">
                            {co.property_name}
                          </h3>
                          <div className="mt-2 space-y-1">
                            <p className="text-sm text-slate-600 flex items-center gap-2">
                              <Euro className="w-4 h-4 text-amber-600" />
                              Prezzo originale: <span className="font-semibold">€{co.original_price}</span>
                            </p>
                            {co.request_details && (
                              <p className="text-sm text-slate-600 flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-blue-600" />
                                {co.request_details.check_in} → {co.request_details.check_out}
                                {co.request_details.guests && <span className="ml-2">• {co.request_details.guests} ospiti</span>}
                              </p>
                            )}
                            {co.message && (
                              <p className="text-sm text-slate-600 bg-slate-50 p-2 rounded mt-2 italic">
                                "{co.message}"
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <div className="text-3xl font-black text-purple-600">€{co.counter_price}</div>
                          <p className="text-xs text-slate-500">proposta viaggiatore</p>
                          {co.discount_percent > 0 && (
                            <Badge className="mt-1 bg-amber-100 text-amber-800">
                              -{co.discount_percent}% sul prezzo
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      {co.status === 'pending' && (
                        <div className="flex gap-3 mt-4 pt-4 border-t">
                          <Button
                            onClick={() => handleAcceptCounterOffer(co.id)}
                            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                            data-testid={`accept-counter-offer-${co.id}`}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Accetta €{co.counter_price}
                          </Button>
                          <Button
                            onClick={() => handleRejectCounterOffer(co.id)}
                            variant="outline"
                            className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
                            data-testid={`reject-counter-offer-${co.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Rifiuta
                          </Button>
                        </div>
                      )}
                      {co.status !== 'pending' && (
                        <div className="mt-4 pt-4 border-t">
                          <Badge className={`
                            ${co.status === 'accepted' ? 'bg-emerald-100 text-emerald-800' : ''}
                            ${co.status === 'rejected' ? 'bg-red-100 text-red-800' : ''}
                          `}>
                            {co.status === 'accepted' && <CheckCircle2 className="w-4 h-4 mr-1 inline" />}
                            {co.status === 'rejected' && <XCircle className="w-4 h-4 mr-1 inline" />}
                            {co.status === 'accepted' ? 'Accettata - Il viaggiatore può procedere al pagamento' : 'Rifiutata'}
                          </Badge>
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* COUNTER-OFFERS ON DEALS (existing) */}
            {dealOffers.length > 0 && (
              <div className="space-y-4 mt-8">
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <Package className="w-5 h-5 text-orange-600" />
                  Contro-offerte sui Deals ({dealOffers.filter(o => o.status === 'pending').length} da valutare)
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  {dealOffers.map((offer) => (
                    <Card key={offer.id} className="p-6 border-l-4 border-l-orange-500">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className="bg-orange-100 text-orange-800">Deal Counter</Badge>
                            {offer.traveler && (
                              <span className="text-sm text-slate-600">da {offer.traveler.full_name}</span>
                            )}
                          </div>
                          <h3 className="font-bold text-lg text-slate-900">
                            {offer.deal_details?.room_type_name || 'Camera'}
                          </h3>
                          <p className="text-sm text-slate-600">
                            Prezzo deal: €{offer.deal_details?.original_price || offer.deal_details?.discounted_price}
                          </p>
                          <p className="text-sm text-slate-600 flex items-center gap-2 mt-1">
                            <Calendar className="w-4 h-4" />
                            {offer.check_in_date} → {offer.check_out_date}
                            <span className="ml-2">• {offer.guests} ospiti</span>
                          </p>
                          {offer.notes && (
                            <p className="text-sm text-slate-600 bg-slate-50 p-2 rounded mt-2 italic">"{offer.notes}"</p>
                          )}
                        </div>
                        <div className="text-right ml-4">
                          <div className="text-3xl font-black text-orange-600">€{offer.offered_price}</div>
                          <p className="text-xs text-slate-500">offerta viaggiatore</p>
                        </div>
                      </div>
                      
                      {offer.status === 'pending' && (
                        <div className="flex gap-3 mt-4 pt-4 border-t">
                          <Button
                            onClick={() => handleAcceptDealOffer(offer.id)}
                            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Accetta
                          </Button>
                          <Button
                            onClick={() => handleRejectDealOffer(offer.id)}
                            variant="outline"
                            className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Rifiuta
                          </Button>
                        </div>
                      )}
                      {offer.status !== 'pending' && (
                        <Badge className={`mt-4 ${
                          offer.status === 'accepted' ? 'bg-emerald-100 text-emerald-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {offer.status === 'accepted' && <CheckCircle2 className="w-3 h-3 mr-1 inline" />}
                          {offer.status !== 'accepted' && <XCircle className="w-3 h-3 mr-1 inline" />}
                          {offer.status === 'accepted' ? 'Accettata' : 'Rifiutata'}
                        </Badge>
                      )}
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {counterOffers.length === 0 && dealOffers.length === 0 && (
              <Card className="p-16 text-center">
                <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 text-lg">Nessuna contro-offerta ricevuta</p>
                <p className="text-slate-500 text-sm mt-2">Quando i viaggiatori faranno contro-offerte sulle tue proposte, appariranno qui</p>
              </Card>
            )}
          </TabsContent>

          {/* AI SUGGESTIONS TAB */}
          <TabsContent value="ai-suggestions" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-purple-600" />
                  Suggerimenti AI
                </h2>
                <p className="text-slate-600 text-sm">Viaggiatori che potrebbero essere interessati alla tua struttura</p>
              </div>
              <Badge className="bg-purple-100 text-purple-800 border-purple-300">
                Powered by GPT-5.2
              </Badge>
            </div>

            {aiSuggestions.length === 0 ? (
              <Card className="p-16 text-center bg-gradient-to-br from-purple-50 to-indigo-50">
                <Sparkles className="w-16 h-16 text-purple-400 mx-auto mb-4" />
                <p className="text-slate-600 text-lg mb-2">Nessun suggerimento al momento</p>
                <p className="text-slate-500 text-sm">L'AI sta analizzando le richieste dei viaggiatori per trovare match perfetti</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {aiSuggestions.map((suggestion) => (
                  <Card key={suggestion.id} className="overflow-hidden hover:shadow-xl transition-all border-l-4 border-l-purple-500">
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
                              <Sparkles className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <h3 className="text-xl font-bold text-slate-900">{suggestion.title}</h3>
                              <p className="text-sm text-slate-600">{suggestion.description}</p>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-3xl font-black text-purple-600 mb-1">{suggestion.match_score}%</div>
                          <Badge className={`
                            ${suggestion.urgency === 'high' ? 'bg-red-100 text-red-800 border-red-300' : ''}
                            ${suggestion.urgency === 'medium' ? 'bg-orange-100 text-orange-800 border-orange-300' : ''}
                            ${suggestion.urgency === 'low' ? 'bg-blue-100 text-blue-800 border-blue-300' : ''}
                          `}>
                            {suggestion.urgency === 'high' ? 'Alta Priorità' : 
                             suggestion.urgency === 'medium' ? 'Media Priorità' : 'Suggerito'}
                          </Badge>
                        </div>
                      </div>

                      {/* Reasons */}
                      <div className="bg-purple-50 rounded-lg p-4 mb-4">
                        <p className="text-xs font-semibold text-purple-900 mb-2">Perché è un buon match:</p>
                        <ul className="space-y-1">
                          {suggestion.reasons?.map((reason, index) => (
                            <li key={index} className="flex items-start gap-2 text-sm text-purple-800">
                              <TrendingUp className="w-4 h-4 mt-0.5 flex-shrink-0" />
                              <span>{reason}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Request details if available */}
                      {suggestion.request && (
                        <div className="bg-slate-50 rounded-lg p-3 mb-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                            <div>
                              <span className="text-slate-500">Città:</span>
                              <span className="font-semibold ml-1">{suggestion.request.city}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">Budget:</span>
                              <span className="font-semibold ml-1">€{suggestion.request.budget_min}-{suggestion.request.budget_max}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">Ospiti:</span>
                              <span className="font-semibold ml-1">{suggestion.request.guests || suggestion.request.num_guests}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">Date:</span>
                              <span className="font-semibold ml-1">{suggestion.request.check_in_date}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Action */}
                      <Button
                        className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 h-12"
                        onClick={() => {
                          if (suggestion.request) {
                            setSelectedRequest(suggestion.request);
                          } else {
                            toast.info('Dettagli richiesta non disponibili');
                          }
                        }}
                      >
                        {suggestion.action_label || "Fai un'Offerta"}
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* SETTINGS/MANAGEMENT TAB */}
          <TabsContent value="settings" className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Gestione Struttura</h2>
              <p className="text-slate-600 text-sm">Gestisci foto, camere e ottimizza le vendite</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Photo Upload Section */}
              <Card className="p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Camera className="w-5 h-5 text-blue-600" />
                  Foto Struttura
                </h3>
                <PhotoUploader 
                  photoType="property" 
                  propertyId={property?.id}
                  onUploadSuccess={() => {
                    toast.success('Foto caricata!');
                    fetchAllData();
                  }}
                  maxPhotos={10}
                />
              </Card>

              {/* Property Info Card */}
              <Card className="p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-orange-600" />
                  Informazioni Struttura
                </h3>
                {property ? (
                  <div className="space-y-4">
                    <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-lg">
                      <p className="text-2xl font-bold text-slate-900">{property.name}</p>
                      <p className="text-sm text-slate-600 flex items-center gap-2 mt-1">
                        📍 {property.city} - {property.address}
                      </p>
                      <p className="text-xs text-slate-500 mt-2">{property.description}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-slate-50 p-3 rounded-lg text-center">
                        <p className="text-2xl font-black text-orange-600">{deals.length}</p>
                        <p className="text-xs text-slate-600">Deals Attivi</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded-lg text-center">
                        <p className="text-2xl font-black text-emerald-600">{myOffers.filter(o => o.status === 'accepted').length}</p>
                        <p className="text-xs text-slate-600">Offerte Accettate</p>
                      </div>
                    </div>
                    <Button variant="outline" className="w-full" onClick={() => toast.info('Modifica struttura in arrivo')}>
                      <Settings className="w-4 h-4 mr-2" />
                      Modifica Informazioni
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-600">Nessuna struttura configurata</p>
                    <Button className="mt-4 bg-orange-600" onClick={() => navigate('/property/register')}>
                      Aggiungi Struttura
                    </Button>
                  </div>
                )}
              </Card>
            </div>

            {/* Unsold Rooms Manager */}
            <Card className="p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-amber-600" />
                Ottimizzazione Camere
              </h3>
              <UnsoldRoomsManager />
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Modals */}
      <CreateDealModal
        open={showCreateDealModal}
        onClose={() => setShowCreateDealModal(false)}
        propertyId={property?.id}
        onSuccess={fetchAllData}
      />

      {selectedRequest && (
        <CreateOfferModal
          open={!!selectedRequest}
          onClose={() => setSelectedRequest(null)}
          request={selectedRequest}
          onSuccess={fetchAllData}
        />
      )}
    </div>
  );
};

export default PropertyOwnerDashboardNew;
